import React, { useState, useEffect, useRef, memo } from 'react';
import './LazyImage.css';

/**
 * LazyImage — only starts loading when the element is near the viewport.
 * Shows a shimmer placeholder until loaded, then fades in.
 *
 * Props:
 *   src           — image URL
 *   alt           — alt text
 *   style         — styles applied to the <img> (e.g. objectFit, width, height)
 *   containerStyle— styles applied to the wrapper div
 *   rootMargin    — how far ahead of the viewport to start loading (default 300px)
 *   scrollRoot    — the scrollable container element (pass when scroll is on a div, not window)
 */
function LazyImage({ src, alt = '', style = {}, containerStyle = {}, rootMargin = '300px', scrollRoot = null }) {
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !src) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { root: scrollRoot || null, rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [src, rootMargin, scrollRoot]);

  // Reset state when src changes
  useEffect(() => {
    setLoaded(false);
    setErrored(false);
    setInView(false);
  }, [src]);

  return (
    <div ref={wrapRef} style={{ position: 'relative', overflow: 'hidden', ...containerStyle }}>
      {/* Shimmer — visible while image hasn't loaded */}
      {!loaded && !errored && (
        <div
          className="lazy-img-shimmer"
          style={{ position: 'absolute', inset: 0 }}
        />
      )}

      {/* Only render <img> once near viewport */}
      {inView && !errored && (
        <img
          src={src}
          alt={alt}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          style={{
            ...style,
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.25s ease',
          }}
        />
      )}

    </div>
  );
}

export default memo(LazyImage);
